import {
  allocateInvoiceRefundShares,
  isRefundableSettlementMethod,
  refundableItems,
  type RefundableSettlementMethod,
  type RefundShare,
} from "./refundAllocation";
import { plateNameAr, substitutionLabel } from "./groupPlates";
import { allocationBasis } from "./partyShares";
import type {
  Invoice,
  InvoiceItem,
  InvoiceRefundEvent,
} from "@/lib/api/types";

/**
 * The refund slip's data, and the two ways it comes to exist.
 *
 * Split out of `RefundReceipt.tsx` so both halves are testable without a DOM:
 * the ORIGINAL, minted at the till the moment the command lands, and the
 * REPRINT, rebuilt from the bill itself so a jammed printer, a reload, a second
 * device or a customer who comes back tomorrow is not the end of a
 * customer-facing tax document (trap 9).
 *
 * **A REPRINT IS PER REFUND.** The invoice read publishes one EVENT per refund
 * COMMAND (`Invoice.refunds`, derived from the money ledger on every read and
 * stored nowhere — R11), so a copy is a copy of ONE refund: its own reference,
 * its own money and split, its own method, its own reason code, its own moment,
 * and exactly the plates that refund returned. Two partial refunds off one
 * cheque reprint as two slips, each naming its own plates, which is what a
 * refund is — a separate event with its own money.
 *
 * That closes a real limitation this module used to describe at length. Before
 * the event existed, the wire carried only the per-plate `refunded` flag (§10)
 * and the bill's `refundTotal`, so a reprint could honestly only be a *copy of
 * the bill's refunds to date*: two refunds came out as one document, stating no
 * reason, no time and no reference because none of the three was recoverable.
 * All three now are.
 *
 * **What a copy still does NOT print, deliberately:** the cashier's free-text
 * reason note (R10, business finding 11) — the internal record, on the manager
 * surfaces and never in a guest's hand — and the identity of whoever authorised
 * the refund. Neither is on the invoice read at all, which is the strongest
 * form that rule can take.
 */

/**
 * One refunded plate, as the paper states it.
 *
 * `nameEn` rides beside `nameAr` and is printed UNDER it, never instead of it
 * (D50/S12) — an Arabic-first document that a tourist or an aggregator can also
 * read.
 */
export type RefundReceiptLine = {
  itemId: string;
  nameAr: string;
  nameEn: string | null;
  /**
   * The plate's ingredient swaps (D61), in each language, or `null` on an
   * ordinary line.
   *
   * SNAPSHOTTED ONTO THE LINE rather than resolved at print time, for the same
   * reason every other field here is: a copy is rebuilt from the bill a week
   * later and must state the plate that was refunded, not the plate the
   * catalogue describes today. Without it a guest refunded for «موهيتو ب V7»
   * was handed a tax document naming a plain mojito — a drink they were never
   * served — while the settle receipt for the very same plate named the swap.
   *
   * No money on this label: the slip's own columns already state what came
   * back, and a "(+15)" beside an ingredient on a REFUND reads as a charge.
   */
  swapAr: string | null;
  swapEn: string | null;
  amount: number;
  netAmount: number;
  serviceChargeAmount: number;
  vatAmount: number;
};

/** A refund, as a customer-facing document. */
export type RefundDocument = {
  invoice: Invoice;
  lines: RefundReceiptLine[];
  /**
   * CASH, CARD or INSTAPAY (D44) — the method the money physically went back by
   * (R6).
   *
   * On a COPY this is read off the REFUND's own ledger rows (`event.methods`)
   * and not off the bill, which is what makes DECISION 3 a non-problem here: a
   * settlement corrected after the refund leaves the BILL naming the wrong
   * method, but the refund event still names the right one, so a per-refund copy
   * prints the true method where a combined copy of the bill had to print none.
   *
   * `null` only when the event names no method this release can honour: a method
   * outside the shared allowlist, or — impossible today under R6, expressible
   * under split tender (B3) — more than one method for one refund. A slip that
   * guessed would tell a customer to expect money by a route it is not coming
   * by.
   *
   * Typed as `RefundableSettlementMethod` rather than spelled out, so the slip
   * and the server's allowlist cannot drift: a method added to one without the
   * other stops compiling here.
   */
  method: RefundableSettlementMethod | null;
  /**
   * The reason CODE's label — on a COPY as on the original, because the event
   * carries the code (R10's closed vocabulary, snapshotted on the ledger row).
   * `null` only for a refund filed before the column existed, or for a code this
   * release has no word for; the caller resolves the label, because this module
   * takes no dependency on the translator.
   */
  reasonLabel: string | null;
  /**
   * When the money moved, as an ISO instant — NEVER a preformatted string.
   *
   * The slip is printed in the STORE's timezone (`RefundReceipt`), because a
   * 21:30 Cairo refund stamped in UTC prints 18:30, and a late-night one prints
   * YESTERDAY'S DATE on a document carrying the tax registration number. §5.9
   * fixed exactly this for the CSV's instant columns.
   *
   * On a COPY it is the LEDGER's `occurred_at` for that refund — the moment the
   * money actually moved, not the moment of reprinting. The copy prints its own
   * printing time too, under its own label, so the two can never be confused.
   */
  refundedAt: string | null;
  /**
   * The SERVER's figure for THIS refund: on an original, the bill's
   * `refundTotal` after the command less what it was before; on a copy, the
   * event's own `amount`, which is the ledger's sum over exactly that refund's
   * rows. Authoritative — see `RefundReceipt`'s note on why the two are
   * compared rather than the client's simply trusted.
   */
  serverTotal: number;
  /**
   * The slip's OWN reference, so two partial refunds off one bill are not two
   * indistinguishable pieces of paper (business finding 4).
   *
   * It is the short form of the refund COMMAND's id — a client-minted UUIDv7
   * that is the primary key of `restaurant_invoice_commands`, so the reference
   * a customer or an accountant quotes can actually be found in the command
   * log.
   *
   * On a COPY it is the SAME reference the original printed, because the copy is
   * a copy of one refund and the event carries that refund's command id. It is
   * never `null` on a document this module builds; the field stays nullable so a
   * caller reading a slip does not have to assume a reference exists.
   */
  reference: string | null;
  /**
   * WHICH REFUND this paper is — the refund command's full id, the primary key
   * of `restaurant_invoice_commands`.
   *
   * `reference` above is the human handle DERIVED from it (its last 8 hex
   * characters), and is not an identity: two commands can in principle share a
   * suffix, and nothing may decide which slip to print by comparing shortened
   * strings. `refundPaperChoice` compares THIS.
   */
  commandId: string;
  /** Rebuilt from the bill rather than minted at the till. */
  isCopy: boolean;
};

/**
 * The paper's reference from the refund command's id.
 *
 * The last 8 hex characters, upper-cased: short enough to be read out over a
 * phone and written on a bill, long enough to find its command by suffix. The
 * FULL id stays the identifier of record — this is a human handle for it, not a
 * new key.
 */
export function refundReference(commandId: string): string {
  return commandId.replace(/-/g, "").slice(-8).toUpperCase();
}

function lineFor(
  item: InvoiceItem,
  share: RefundShare | null,
): RefundReceiptLine {
  return {
    itemId: item.id,
    nameAr: plateNameAr(item),
    nameEn: item.nameEn ?? null,
    // No `money` argument: see `swapAr` on the type.
    swapAr: substitutionLabel(item, undefined, "ar"),
    swapEn: substitutionLabel(item, undefined, "en"),
    amount: share?.amount ?? 0,
    netAmount: share?.netAmount ?? 0,
    serviceChargeAmount: share?.serviceChargeAmount ?? 0,
    vatAmount: share?.vatAmount ?? 0,
  };
}

/**
 * The shares of every chargeable plate on a bill.
 *
 * The basis is the WHOLE chargeable set (the policy's property 4) in both
 * builders below, so a plate's split reads the same on the sheet, on the
 * original slip and on a copy printed a week later.
 */
function sharesFor(invoice: Invoice): {
  plates: InvoiceItem[];
  shares: Map<string, RefundShare> | null;
} {
  const plates = refundableItems(invoice);
  const shares = allocateInvoiceRefundShares(
    // D146: pro-rated onto a discounted bill's food base.
    allocationBasis(invoice),
    plates.map((item) => ({ id: item.id, lineTotal: item.lineTotal })),
  );
  return { plates, shares };
}

/**
 * The ORIGINAL slip, at the moment the command landed.
 *
 * `invoice` is the bill as this device read it BEFORE the refund — its stored
 * figures are unchanged by a refund (R5), so the split is identical either way,
 * and its `refundTotal` is what `serverTotal` is measured against by the caller.
 */
export function buildRefundDocument(input: {
  invoice: Invoice;
  itemIds: readonly string[];
  method: RefundableSettlementMethod;
  reasonLabel: string;
  refundedAt: string;
  serverTotal: number;
  commandId: string;
}): RefundDocument {
  const { plates, shares } = sharesFor(input.invoice);
  return {
    invoice: input.invoice,
    method: input.method,
    lines: input.itemIds.map((itemId) => {
      const item = plates.find((plate) => plate.id === itemId);
      const share = shares?.get(itemId) ?? null;
      return item
        ? lineFor(item, share)
        : {
            itemId,
            nameAr: itemId,
            nameEn: null,
            swapAr: null,
            swapEn: null,
            amount: share?.amount ?? 0,
            netAmount: share?.netAmount ?? 0,
            serviceChargeAmount: share?.serviceChargeAmount ?? 0,
            vatAmount: share?.vatAmount ?? 0,
          };
    }),
    reasonLabel: input.reasonLabel,
    refundedAt: input.refundedAt,
    serverTotal: input.serverTotal,
    reference: refundReference(input.commandId),
    commandId: input.commandId,
    isCopy: false,
  };
}

/**
 * The refunds of a bill, NEWEST FIRST — what a reprint control is a list of.
 *
 * The wire's order is oldest first (one canonical order, server-side); a cashier
 * or a manager reaching for a reprint wants the one that just happened, so the
 * reversal lives here, once, rather than in each of the two screens that offer
 * the control.
 *
 * `?? []` and not a bare read: a bill served by a release older than the refund
 * event carries no `refunds` member, and the honest answer for such a bill is
 * "nothing to reprint" rather than a crash on the till.
 */
export function refundEventsNewestFirst(
  // `Pick`, not the whole `Invoice`: ordering refund events needs the events
  // and nothing else, and the narrower parameter is what lets the bill's
  // per-refund marker rows be tested against a literal instead of a fabricated
  // whole bill.
  invoice: Pick<Invoice, "refunds">,
): InvoiceRefundEvent[] {
  return [...(invoice.refunds ?? [])].reverse();
}

/**
 * One reprint CONTROL, as the two screens that offer them draw it.
 *
 * `named` is the whole of the label rule, extracted here so it is a tested
 * branch rather than a ternary duplicated inside two components — which is where
 * two of the eight tests that could not fail in this change were found hiding.
 *
 * ONE refund on the bill: the plain «اطبع إيصال المرتجع», the label a cashier
 * already knows, with no reference to read. TWO OR MORE: every button names its
 * own reference and amount, because two identical print buttons over one bill is
 * a cashier handing a customer the wrong tax document and learning about it from
 * the customer.
 */
export type RefundSlipButton = {
  commandId: string;
  /** The short handle printed on that refund's own paper. */
  reference: string;
  /** THIS refund's money — a positive magnitude, as the event states it. */
  amount: number;
  /** Label this button with its reference and amount, rather than plainly. */
  named: boolean;
};

/**
 * The reprint controls a bill deserves, newest first.
 *
 * `[]` on a bill with no refund — and on a bill served by a release older than
 * the refund event, which is the same answer for the same reason: there is
 * nothing this screen can honestly put on paper.
 */
export function refundSlipButtons(invoice: Invoice): RefundSlipButton[] {
  const events = refundEventsNewestFirst(invoice);
  return events.map((event) => ({
    commandId: event.commandId,
    reference: refundReference(event.commandId),
    amount: event.amount,
    named: events.length > 1,
  }));
}

/**
 * The METHOD a refund event states, or `null` if it states none this release can
 * honour.
 *
 * Extracted rather than inlined because it is a rule about a tax document and
 * not a rendering detail: exactly one method, and that method on the shared
 * allowlist, or the slip says nothing. More than one method for one refund is
 * impossible under R6 today and expressible under split tender (B3) — a slip
 * that picked one of two would tell a customer to expect money by a route half
 * of it is not coming by.
 */
export function refundEventMethod(
  event: InvoiceRefundEvent,
): RefundableSettlementMethod | null {
  const methods = event.methods ?? [];
  if (methods.length !== 1) return null;
  const only = methods[0];
  return isRefundableSettlementMethod(only) ? only : null;
}

/**
 * A COPY OF ONE REFUND, rebuilt from the bill — the answer to "the printer
 * jammed" and to "the customer came back tomorrow", on any device and after any
 * reload, and now naming exactly the refund it is a copy of.
 *
 * An OBJECT parameter with three required keys, deliberately: this project
 * compiles with `strictNullChecks: false`, so adding `refund` and `reasonLabel`
 * as positional parameters beside `invoice` would let a call site pass them in
 * the wrong order — or omit them — and still compile, which is the exact trap an
 * earlier phase of this change hit. A missing PROPERTY is a structural error the
 * compiler still catches with strict null checks off.
 *
 * `reasonLabel` is passed IN rather than derived: resolving R10's code to words
 * needs the translator, and this module deliberately has no dependency on it —
 * the same division `buildRefundDocument` already makes.
 *
 * `null` when the event names no plate this bill still has — a read from a
 * release older than §10, or an event whose plates were all non-chargeable,
 * neither of which the product can currently produce. A copy naming no plates is
 * not a document, so it is refused rather than printed empty.
 */
export function buildRefundReprint(input: {
  invoice: Invoice;
  refund: InvoiceRefundEvent;
  reasonLabel: string | null;
}): RefundDocument | null {
  const { plates, shares } = sharesFor(input.invoice);
  // The plates THIS refund covered, in the bill's own plate order rather than
  // the ledger's: the copy and the original then list them the same way, and a
  // guest comparing two pieces of paper is not reading a reordered document.
  const covered = plates.filter((item) =>
    input.refund.itemIds.includes(item.id),
  );
  if (covered.length === 0) return null;
  return {
    invoice: input.invoice,
    // Off the REFUND's rows, never off the bill. DECISION 3 stops being a
    // limitation here: a settlement corrected after the refund leaves the BILL
    // naming the wrong method while the event still names the right one.
    method: refundEventMethod(input.refund),
    lines: covered.map((item) => lineFor(item, shares?.get(item.id) ?? null)),
    reasonLabel: input.reasonLabel,
    // The LEDGER's `occurred_at` for this refund — when the money actually
    // moved. The copy also prints its own printing time, under its own label,
    // so the two can never be read as each other.
    refundedAt: input.refund.occurredAt,
    // The ledger's own figure for THIS refund, not for the bill.
    serverTotal: input.refund.amount,
    // The SAME reference the original printed: one refund, one reference,
    // however many times it is put on paper.
    reference: refundReference(input.refund.commandId),
    commandId: input.refund.commandId,
    isCopy: true,
  };
}

/**
 * The slip's instant, in the STORE's timezone (business DEFECT-2).
 *
 * Western digits in both locales, the app-wide rule (`ar-EG-u-nu-latn`), and the
 * same `Intl` shape `useInvoiceDateFormat` uses — the one difference is the
 * zone, which is the store's and not the device's: a refund slip is a tax
 * document about a restaurant, not about the tablet it was printed from.
 *
 * A missing or unusable timezone falls back to the device's zone rather than
 * throwing: a slip with a plausible local time beats a white screen at the till.
 */
export function formatRefundStamp(
  instant: string,
  timezone: string | null | undefined,
  locale: "ar" | "en",
): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return instant;
  const tag = locale === "ar" ? "ar-EG-u-nu-latn" : "en-EG";
  const options: Intl.DateTimeFormatOptions = {
    dateStyle: "short",
    timeStyle: "short",
  };
  if (timezone) {
    try {
      return new Intl.DateTimeFormat(tag, {
        ...options,
        timeZone: timezone,
      }).format(at);
    } catch {
      // An unknown zone string throws a RangeError. Fall through to the
      // device's zone rather than losing the stamp altogether.
    }
  }
  return new Intl.DateTimeFormat(tag, options).format(at);
}
